import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { apiCall } from '../services/api';

const AuthContext = createContext(null);

// Decode JWT token to extract payload (username, user_id)
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Failed to decode token:', e);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = () => {
      const savedToken = localStorage.getItem('auth_token');
      const savedUsername = localStorage.getItem('username');
      
      console.log('🔐 AuthContext init - token exists:', !!savedToken, 'username:', savedUsername);
      
      if (savedToken) {
        // Decode token to get user info
        const decoded = decodeToken(savedToken);
        console.log('🔐 Decoded token:', decoded);
        
        if (decoded) {
          // Check if token is expired
          const now = Math.floor(Date.now() / 1000);
          if (decoded.exp && decoded.exp > now) {
            const userId = decoded.user_id || decoded.sub || savedUsername;
            console.log('🔐 Setting user with userId:', userId);
            setUser({
              username: savedUsername || decoded.email || decoded.name || 'User',
              userId: userId,
              email: decoded.email,
              name: decoded.name,
              token: savedToken
            });
          } else {
            console.log('🔐 Token expired, clearing auth');
            // Token expired, clear auth
            localStorage.removeItem('auth_token');
            localStorage.removeItem('username');
          }
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = useCallback((userData) => {
    // userData should contain: { access_token, username }
    const { access_token, username } = userData;
    
    // Decode token to extract user_id
    const decoded = decodeToken(access_token);
    const userId = decoded?.user_id || username;
    
    // Save to localStorage
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('username', username);
    
    // Update state
    setUser({
      username,
      userId,
      token: access_token
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('username');
    setUser(null);
  }, []);

  // Get the current userId (for use in API calls)
  const getUserId = useCallback(() => {
    return user?.userId || user?.username || null;
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, getUserId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper hook to get just the userId
export const useUserId = () => {
  const { getUserId, user } = useAuth();
  return getUserId() || user?.username || null; // No fallback - require actual user ID
};
